import { useState } from "react";
import {
  ChevronDown,
  Download,
  Loader2,
  Lock,
  RotateCcw,
  Thermometer,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUserDetails,
  useGenerateTemperatureReport,
  useConfiguration,
  useRequestTempPresignedUrls,
  usePutPresignedUrls,
} from "@/hooks";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PRIMARY_COLOR,
  HEADING_COLOR,
  GOOD_COLOR,
  WARNING_COLOR,
  BAD_COLOR,
  DEFAULT_OPTIONS,
  PAPER_SIZES,
  TIMEZONES,
} from "@/lib/constants";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  generateTemperatureReportSchema,
  GenerateTemperatureReportRequest,
} from "@/api/generate-temperature-report";
import {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { useDropzone } from "react-dropzone";
import { parseExcelData } from "@/lib/schemas/temperature";
import { toast } from "sonner";
import posthog from "posthog-js";

const TemperaturePage = () => {
  const { data: user } = useUserDetails();
  const { data: configuration } = useConfiguration();
  const { mutateAsync: generateTemperatureReport, isPending: isLoading } =
    useGenerateTemperatureReport();
  const { mutateAsync: requestTempPresignedUrls } =
    useRequestTempPresignedUrls();
  const { mutateAsync: putPresignedUrl } = usePutPresignedUrls();

  const [processing, setProcessing] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
    multiple: false,
    onDrop: async (acceptedFiles) => {
      setProcessing(true);

      const getKey = async () => {
        const [{ url, key }] = await requestTempPresignedUrls({ amount: 1 });
        await putPresignedUrl({ url, file: acceptedFiles[0] });
        return key;
      };

      const [data, key] = await Promise.all([
        parseExcelData(acceptedFiles[0]),
        getKey(),
      ]);

      if (data.err) {
        console.error(data.val);
        const errors = data.val.map((e) => ({
          errors: e.zodError.error.issues,
          sheetName: e.sheetName,
          rowNumber: e.rowNumber,
        }));
        errors.forEach((e) => {
          e.errors.forEach((error) => {
            toast.error(
              `En la hoja ${e.sheetName} hay un error en la fila ${e.rowNumber}`,
              {
                description: error.message,
                duration: 1000 * 30,
              }
            );
          });
        });
      } else {
        form.setValue("excelData", data.val);
        form.setValue("tmpFileKey", key);
        form.setValue("inputFilename", acceptedFiles[0].name);
      }
      setProcessing(false);
    },
  });

  // todo: find a better way to fill in form with async values
  const form = useForm<GenerateTemperatureReportRequest>({
    resolver: zodResolver(generateTemperatureReportSchema),
    values: {
      bodyTemperatureCelsius:
        configuration?.tBodyTemperatureC ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.BODY_TEMPERATURE_CELSIUS),
      bodyTemperatureShiftCelsius:
        configuration?.tBodyTemperatureShiftC ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.BODY_TEMPERATURE_SHIFT_CELSIUS),
      temperatureRounding:
        configuration?.tTemperatureRounding ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.TEMPERATURE_ROUNDING),
      primaryColor: configuration?.primaryColor ?? PRIMARY_COLOR,
      headingColor: configuration?.headingColor ?? HEADING_COLOR,
      goodColor: configuration?.goodColor ?? GOOD_COLOR,
      warningColor: configuration?.warningColor ?? WARNING_COLOR,
      badColor: configuration?.badColor ?? BAD_COLOR,
      paperSize: configuration?.paperSize ?? DEFAULT_OPTIONS.GENERAL.PAPER_SIZE,
      marginInches:
        configuration?.marginIn ??
        Number(DEFAULT_OPTIONS.GENERAL.MARGIN_INCHES),
      timezone: configuration?.timezone ?? DEFAULT_OPTIONS.GENERAL.TIMEZONE,
      password: "",
      reportTier: "free",
      // @ts-expect-error: excelData is not required
      excelData: undefined,
      // @ts-expect-error: inputFilename is not required
      inputFilename: undefined,
      // @ts-expect-error: tmpFileKey is not required
      tmpFileKey: undefined,
      coverImage:
        configuration?.coverImage ?? DEFAULT_OPTIONS.GENERAL.COVER_IMAGE,
      optionalId: "",
      outline: configuration?.outline ?? DEFAULT_OPTIONS.GENERAL.OUTLINE,
      sectionDividers:
        configuration?.sectionDividers ??
        DEFAULT_OPTIONS.GENERAL.SECTION_DIVIDERS,
    },
  });

  const [downloadFileType, setDownloadFileType] = useState("xlsx");

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-end gap-6">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Temperatura</h1>
          <p className="text-xs text-muted-foreground">
            Llene la plantilla y suba el archivo para generar un reporte de
            temperatura
          </p>
        </div>
        <div className="flex gap-[2px]">
          {downloadFileType === "xlsx" ? (
            <a
              href="/excel-templates/temperature/Bernardino_Plantilla_TGBH.xlsx"
              download
            >
              <Button variant="success" className="rounded-r-none w-36">
                <Download /> Plantilla (.xlsx)
              </Button>{" "}
            </a>
          ) : (
            <a
              href="/excel-templates/temperature/Bernardino_Plantilla_TGBH_EXCEL97.xls"
              download
            >
              <Button variant="success" className="rounded-r-none w-36">
                <Download /> Plantilla (.xls)
              </Button>{" "}
            </a>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="success" className="rounded-l-none" size="icon">
                <ChevronDown />{" "}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 font-mono">
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => setDownloadFileType("xlsx")}
                  data-state={
                    downloadFileType === "xlsx" ? "active" : "inactive"
                  }
                  className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none data-[state=active]:border-l-2 border-success"
                >
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDownloadFileType("xls")}
                  data-state={
                    downloadFileType === "xls" ? "active" : "inactive"
                  }
                  className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none data-[state=active]:border-l-2 border-success"
                >
                  Excel97-2003 (.xls)
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Separator />
      <div className="grid gap-1">
        <Form {...form}>
          <form
            id="temperature-form"
            onSubmit={form.handleSubmit(async (values) => {
              if (values.reportTier === "free") {
                posthog.capture("generate_temperature_free");
              } else {
                posthog.capture("generate_temperature_paid");
              }
              const url = await generateTemperatureReport(values);
              window.open(url, "_blank");
            })}
            className="space-y-6"
          >
            <div className="grid gap-2">
              <div className="flex gap-1 flex-wrap">
                <div className="flex gap-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-r-none rounded-l-md border-1 border-r-0"
                      >
                        Constantes
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="bodyTemperatureCelsius"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Temperatura corporal
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="bodyTemperatureShiftCelsius"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Desplazamiento de temperatura corporal
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-none border-y-1"
                      >
                        Decimales
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="temperatureRounding"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Presición de temperatura
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-none border-y-1"
                      >
                        Colores
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="primaryColor"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormControl>
                                  <Input
                                    type="color"
                                    className="hidden"
                                    {...field}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-medium">
                                  <span
                                    className="rounded-full size-6"
                                    style={{ backgroundColor: field.value }}
                                  ></span>
                                  Primario
                                </FormLabel>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="headingColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Encabezado
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="goodColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Aceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="warningColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Alerta
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="badColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Inaceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-l-none rounded-r-md border-1 border-l-0"
                      >
                        Tamaños
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="paperSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Tipo de hoja
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Tamaño" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {PAPER_SIZES.map((size) => (
                                    <SelectItem
                                      key={size.value}
                                      value={size.value}
                                    >
                                      {size.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="marginInches"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Margen
                              </FormLabel>
                              <FormControl>
                                <Input
                                  step={0.1}
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <FormField
                  control={form.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Zona horaria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIMEZONES.map((timezone) => (
                            <SelectItem
                              key={timezone.value}
                              value={timezone.value}
                            >
                              {timezone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="hover:bg-muted/5 focus:bg-muted/5 rounded-md border-1 text-foreground"
                      size="icon"
                    >
                      <Lock />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="text-foreground">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Contraseña
                            </FormLabel>
                            <FormControl>
                              <Input type="text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <FormLabel className="text-xs font-medium">Opciones</FormLabel>
                <div className="grid gap-1">
                  <FormField
                    control={form.control}
                    name="coverImage"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir imagen de portada
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="outline"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir tabla de contenido
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sectionDividers"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir portadas de secciones
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <FormField
              control={form.control}
              name="excelData"
              render={() => (
                <FormItem>
                  <FormControl>
                    {!form.watch("excelData") ? (
                      <div
                        {...getRootProps()}
                        className="w-full h-[250px] border-2 border-dashed rounded-md bg-accent/10 border-accent cursor-pointer"
                      >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center justify-center h-full text-center w-full">
                          {processing ? (
                            <>
                              <div className="flex items-center gap-2 mb-6">
                                <Loader2 className="size-10 text-primary/60 animate-spin" />
                              </div>
                              <p className="text-sm font-medium text-foreground/80">
                                Se están procesando los datos del archivo Excel
                              </p>
                              <p className="text-xs text-muted-foreground/80">
                                Esto puede tardar unos segundos
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 mb-6">
                                <Thermometer className="size-10 text-primary/60" />
                                <p className="text-4xl font-bold text-primary/60">
                                  Temperatura
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Upload className="size-4 text-foreground/80" />
                                <p className="text-sm font-medium text-foreground/80">
                                  {isDragActive
                                    ? "Suelte el archivo aquí"
                                    : "Arrastre el archivo aquí o haga clic para seleccionar"}
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground/80">
                                Máximo 1 archivo (xlsx/xls)
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full border rounded bg-accent/5 p-4">
                        <Tabs defaultValue="administrative" className="w-full">
                          <TabsList className="grid w-full grid-cols-5">
                            <TabsTrigger value="administrative">
                              Datos Admin
                            </TabsTrigger>
                            <TabsTrigger value="evaluations">
                              Evaluaciones
                            </TabsTrigger>
                            <TabsTrigger value="recognitions">
                              Fuentes Térmicas
                            </TabsTrigger>
                            <TabsTrigger value="exposure">
                              Grupos Exp.
                            </TabsTrigger>
                            <TabsTrigger value="bodytemperature">
                              Temp. Corporal
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent
                            value="administrative"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.administrativeData ? (
                              <div className="space-y-3">
                                {Object.entries(
                                  form.getValues("excelData")
                                    ?.administrativeData || {}
                                ).map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex flex-col space-y-1"
                                  >
                                    <label className="text-sm font-medium capitalize text-foreground">
                                      {key.replace(/([A-Z])/g, " $1").trim()}
                                    </label>
                                    <span className="text-sm text-muted-foreground">
                                      {value || "No proporcionado"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay datos administrativos disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="evaluations"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.evaluationRows &&
                            form.getValues("excelData")!.evaluationRows.length >
                              0 ? (
                              <div className="grid gap-1">
                                <div className="flex items-center gap-1">
                                  <h6 className="text-xs font-medium">
                                    Fechas:
                                  </h6>
                                  {Array.from(
                                    new Set(
                                      form
                                        .getValues("excelData")!
                                        .evaluationRows.map(
                                          (evaluation) =>
                                            evaluation["fecha"] ?? ""
                                        )
                                        .filter(Boolean)
                                    )
                                  ).map((date) => (
                                    <Badge
                                      key={date}
                                      className="text-[10px] px-2 py-0.5"
                                    >
                                      {date}
                                    </Badge>
                                  ))}
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Ubicación
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Identificación
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Puesto
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Temp. Inicial
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Temp. Final
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .evaluationRows.map(
                                        (evaluation, index) => (
                                          <TableRow
                                            key={index}
                                            className="hover:bg-accent/5"
                                          >
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["departamento"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(evaluation["area"] ?? "")}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["ubicacion"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["identificacion"] ??
                                                  ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["puesto"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {evaluation[
                                                "temperatura axilar inicial (°c)"
                                              ] ?? "N/A"}
                                              °C
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {evaluation[
                                                "temperatura axilar final (°c)"
                                              ] ?? "N/A"}
                                              °C
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={7}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .evaluationRows.length
                                        }{" "}
                                        evaluaciones
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay evaluaciones disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="recognitions"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.recognitionRows &&
                            form.getValues("excelData")!.recognitionRows
                              .length > 0 ? (
                              <div className="grid gap-1">
                                <div className="flex items-center gap-1">
                                  <h6 className="text-xs font-medium">
                                    Fechas:
                                  </h6>
                                  {Array.from(
                                    new Set(
                                      form
                                        .getValues("excelData")!
                                        .recognitionRows.map(
                                          (recognition) =>
                                            recognition["fecha"] ?? ""
                                        )
                                        .filter(Boolean)
                                    )
                                  ).map((date) => (
                                    <Badge
                                      key={date}
                                      className="text-[10px] px-2 py-0.5"
                                    >
                                      {date}
                                    </Badge>
                                  ))}
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Fuentes Térmicas
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Descripción del Espacio
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Tipo de Ventilación
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .recognitionRows.map(
                                        (recognition, index) => (
                                          <TableRow
                                            key={index}
                                            className="hover:bg-accent/5"
                                          >
                                            <TableCell className="text-xs">
                                              {recognition["departamento"] ??
                                                ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition["area"] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition[
                                                "fuentes termicas"
                                              ] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition[
                                                "descripcion del espacio"
                                              ] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition[
                                                "tipo de ventilacion"
                                              ] ?? ""}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={5}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .recognitionRows.length
                                        }{" "}
                                        fuentes térmicas
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay fuentes térmicas disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="exposure"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.exposureGroupsRows &&
                            form.getValues("excelData")!.exposureGroupsRows
                              .length > 0 ? (
                              <div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Puesto
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        POE
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Tipo de Puesto
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Régimen de Trabajo
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .exposureGroupsRows.map(
                                        (group, index) => (
                                          <TableRow
                                            key={index}
                                            className="hover:bg-accent/5"
                                          >
                                            <TableCell className="text-xs">
                                              {String(
                                                group["departamento"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(group["area"] ?? "")}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(group["puesto"] ?? "")}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {group["poe"] ?? "N/A"}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {group["tipo de puesto"] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {group["regimen de trabajo"] ??
                                                ""}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .exposureGroupsRows.length
                                        }{" "}
                                        grupos de exposición
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay grupos de exposición disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="bodytemperature"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.bodyTemperatures &&
                            form.getValues("excelData")!.bodyTemperatures
                              .length > 0 ? (
                              <div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Identificación
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Temp. Inicio Turno
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Temp. Fin Turno
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Termómetro
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .bodyTemperatures.map((temp, index) => (
                                        <TableRow
                                          key={index}
                                          className="hover:bg-accent/5"
                                        >
                                          <TableCell className="text-xs">
                                            {String(temp["departamento"] ?? "")}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {String(temp["area"] ?? "")}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {String(
                                              temp["identificacion"] ?? ""
                                            )}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {temp[
                                              "temperatura axilar inicio del turno (°c)"
                                            ] ?? "N/A"}
                                            °C
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {temp[
                                              "temperatura axilar fin del turno (°c)"
                                            ] ?? "N/A"}
                                            °C
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {temp["termometro marca"] ?? ""}{" "}
                                            {temp["termometro modelo"] ?? ""}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .bodyTemperatures.length
                                        }{" "}
                                        mediciones de temperatura corporal
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay mediciones de temperatura corporal
                                disponibles
                              </p>
                            )}
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-between">
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant="destructive"
                        type="button"
                        disabled={isLoading || !form.watch("excelData")}
                        className="gap-2"
                        onClick={() => form.reset()}
                      >
                        <RotateCcw className="size-4" />
                        Resetear
                      </Button>
                    </TooltipTrigger>
                    {!form.watch("excelData") && (
                      <TooltipContent>
                        <p>
                          Selecciona al menos un archivo para resetear
                          formulario
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant="secondary"
                        type="submit"
                        disabled={isLoading || !form.watch("excelData")}
                        className="w-32 gap-2"
                        onClick={() => form.setValue("reportTier", "free")}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Procesando...
                          </>
                        ) : (
                          "Preliminar"
                        )}
                      </Button>
                    </TooltipTrigger>
                    {!form.watch("excelData") && (
                      <TooltipContent>
                        <p>
                          Selecciona al menos un archivo para generar reporte
                          preliminar
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            disabled={
                              isLoading ||
                              !form.watch("excelData") ||
                              user?.type !== "subscribed"
                            }
                            className="w-32 gap-2"
                          >
                            Final
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Id de Proyecto</DialogTitle>
                            <DialogDescription>
                              Puede ingresar un identificador para el proyecto.
                              Este sera incluido en el reporte.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4">
                            <FormField
                              control={form.control}
                              name="optionalId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm font-medium">
                                    Identificador
                                  </FormLabel>
                                  <FormControl>
                                    <Input type="text" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button
                                variant="outline"
                                type="button"
                                disabled={isLoading}
                                className="w-32 gap-2 border-destructive text-destructive focus:bg-transparent hover:bg-transparent bg-transparent hover:text-destructive focus:text-destructive"
                              >
                                Cancelar
                              </Button>
                            </DialogClose>
                            <Button
                              form="temperature-form"
                              type="submit"
                              disabled={isLoading || !form.watch("excelData")}
                              className="w-32 gap-2"
                              onClick={() => {
                                form.setValue("reportTier", "paid");
                              }}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="size-4 animate-spin" />
                                  Procesando...
                                </>
                              ) : (
                                "Final"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </TooltipTrigger>
                    {(!form.watch("excelData") ||
                      user?.type !== "subscribed") && (
                      <TooltipContent>
                        {user?.type !== "subscribed" ? (
                          <p>Solo disponible para usuarios con suscripción</p>
                        ) : (
                          <p>
                            Selecciona al menos un archivo para generar reporte
                            final
                          </p>
                        )}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default TemperaturePage;
